import React, { useEffect, useRef, useState } from 'react';
import { signOut, updateProfile, deleteUser as firebaseDeleteUser } from 'firebase/auth';
import { getAuth } from 'firebase/auth';
import { useNavigate, useSearchParams } from 'react-router';
import { FaSignOutAlt } from 'react-icons/fa';
import { MdSave } from 'react-icons/md';
import { app } from '../utils/firebase';
import { useAppContext } from './AppContext';
import ActionButton from './ActionButton';
import SelectComponent from './SelectComponent';
import History from './History';
import Friends from './Friends';
import Camera from './Camera';
import { FontIcon, IconButton } from '@fluentui/react';
import SConsciousApiService from '../services/SConsciousApiService';
import { IContribution } from '../../../shared/interfaces/IContribution';

const _sConsciousApiService = new SConsciousApiService();

interface UserProps {}

export default function User({}: UserProps) {
    const { appData, setAppData } = useAppContext();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const auth = getAuth(app);
    const [currencies, setCurrencies] = useState<{ code: string; name: string }[]>([]);
    const [editName, setEditName] = useState(appData.user.userName);
    const [editCurrency, setEditCurrency] = useState(appData.user.currency);
    const [editDiscoverable, setEditDiscoverable] = useState(!!appData.user.discoverable);
    const [saving, setSaving] = useState(false);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);

    // Fidelity cards state
    interface FidelityCard { blobName: string; url: string; name: string; createdOn: string; }
    const [fidelityCards, setFidelityCards] = useState<FidelityCard[]>([]);
    const [cardsLoading, setCardsLoading] = useState(false);
    const [lightboxCard, setLightboxCard] = useState<FidelityCard | null>(null);
    const [pendingCardImg, setPendingCardImg] = useState<string | null>(null);
    const [pendingCardName, setPendingCardName] = useState('');
    const [savingCard, setSavingCard] = useState(false);
    const [showCardCamera, setShowCardCamera] = useState(false);
    const [confirmDeleteCard, setConfirmDeleteCard] = useState<FidelityCard | null>(null);

    // sCoN's contributions state
    const [scons, setScons] = useState<{ contributions: IContribution[]; totalPoints: number } | null>(null);
    const [sconsLoading, setSconsLoading] = useState(false);

    // Self-profile stats (public view)
    interface SelfProfileStats {
        receipts_count: number;
        items_count: number;
        scons_points: number;
        friends_count: number;
    }
    const [selfStats, setSelfStats] = useState<SelfProfileStats | null>(null);

    useEffect(() => {
        const CACHE_KEY = 'currencies_cache';
        const CACHE_TS_KEY = 'currencies_cache_ts';
        const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 1 week

        const parseCached = (raw: string | null) => {
            try { return raw ? JSON.parse(raw) as { code: string; name: string }[] : null; }
            catch { return null; }
        };

        const fromApi = () =>
            fetch('/api/currencies')
                .then(r => r.json())
                .then((data: Record<string, string>) => {
                    const list = Object.entries(data).map(([code, name]) => ({ code, name }));
                    localStorage.setItem(CACHE_KEY, JSON.stringify(list));
                    localStorage.setItem(CACHE_TS_KEY, String(Date.now()));
                    return list;
                });

        const cached = parseCached(localStorage.getItem(CACHE_KEY));
        const cachedAt = Number(localStorage.getItem(CACHE_TS_KEY) || 0);
        const stale = Date.now() - cachedAt > CACHE_TTL_MS;

        if (cached) {
            setCurrencies(cached);
            if (stale) fromApi().then(setCurrencies).catch(() => {});
        } else {
            fromApi().then(setCurrencies).catch(() => {});
        }
    }, []);

    useEffect(() => {
        if (appData.user.userId) {
            _sConsciousApiService.getUserProfile(appData.user.userId, appData.user.userId)
                .then(data => {
                    if (data) setSelfStats({
                        receipts_count: data.receipts_count ?? 0,
                        items_count: data.items_count ?? 0,
                        scons_points: data.scons_points ?? 0,
                        friends_count: data.friends_count ?? 0,
                    });
                })
                .catch(() => {});
        }
    }, [appData.user.userId]);

    useEffect(() => {
        if (appData.user.userName) setEditName(appData.user.userName);
    }, [appData.user.userName]);

    useEffect(() => {
        if (appData.user.currency) setEditCurrency(appData.user.currency);
    }, [appData.user.currency]);

    const handleCurrencyChange = (code: string) => {
        setEditCurrency(code);
    };

    const handleSaveProfile = async () => {
        if (!editCurrency) {
            alert('Please select a home currency before saving.');
            return;
        }
        const isNewUser = !appData.user.currency;
        setSaving(true);
        try {
            const currentUser = auth.currentUser;
            if (currentUser && editName !== currentUser.displayName) {
                await updateProfile(currentUser, { displayName: editName });
            }
            localStorage.setItem('currency', editCurrency);
            localStorage.setItem('user_name', editName);
            setAppData({ ...appData, user: { ...appData.user, currency: editCurrency, userName: editName, discoverable: editDiscoverable } });
            await fetch('/api/updateUser', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId: appData.user.userId, currency: editCurrency, user_name: editName, discoverable: editDiscoverable })
            });
            if (isNewUser) {
                fetch('/api/provision-welcome', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ userId: appData.user.userId, currency: editCurrency })
                }).catch(() => {}); // fire-and-forget
                navigate('/');
            }
        } catch (err) {
            console.error('Error saving profile:', err);
        } finally {
            setSaving(false);
        }
    };

    const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !appData.user.userId) return;
        setUploadingPhoto(true);
        try {
            const reader = new FileReader();
            reader.onload = async (ev) => {
                const imgSrc = ev.target?.result as string;
                if (!imgSrc) return;
                const result = await _sConsciousApiService.uploadProfilePhoto(appData.user.userId, imgSrc);
                if (result?.url) {
                    setAppData({ ...appData, user: { ...appData.user, photoUrl: result.url } });
                }
                setUploadingPhoto(false);
            };
            reader.readAsDataURL(file);
        } catch (err) {
            console.error('Error uploading profile photo:', err);
            setUploadingPhoto(false);
        }
        // Reset input so the same file can be re-selected
        e.target.value = '';
    };

    const loadFidelityCards = async () => {
        if (!appData.user.userId) return;
        setCardsLoading(true);
        const cards = await _sConsciousApiService.listFidelityCards(appData.user.userId);
        setFidelityCards(cards);
        setCardsLoading(false);
    };

    const [deletingPhoto, setDeletingPhoto] = useState(false);
    const [showPhotoLightbox, setShowPhotoLightbox] = useState(false);
    const [confirmDeletePhoto, setConfirmDeletePhoto] = useState(false);

    const handleDeletePhoto = async () => {
        if (!appData.user.userId) return;
        setDeletingPhoto(true);
        const ok = await _sConsciousApiService.deleteProfilePhoto(appData.user.userId);
        if (ok) {
            setAppData({ ...appData, user: { ...appData.user, photoUrl: '' } });
        }
        setDeletingPhoto(false);
    };

    const handleCardFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            setPendingCardImg(ev.target?.result as string);
            setPendingCardName('');
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    const handleCardCapture = (imgSrc: string) => {
        setShowCardCamera(false);
        setPendingCardImg(imgSrc);
        setPendingCardName('');
    };

    const handleSaveCard = async () => {
        if (!pendingCardImg || !appData.user.userId) return;
        setSavingCard(true);
        const result = await _sConsciousApiService.uploadFidelityCard(appData.user.userId, pendingCardImg, pendingCardName.trim());
        if (result) {
            setFidelityCards(prev => [...prev, { blobName: result.blobName, url: result.url, name: result.name, createdOn: new Date().toISOString() }]);
        }
        setPendingCardImg(null);
        setPendingCardName('');
        setSavingCard(false);
    };

    const handleDeleteCard = async (card: FidelityCard) => {
        await _sConsciousApiService.deleteFidelityCard(appData.user.userId, card.blobName);
        setFidelityCards(prev => prev.filter(c => c.blobName !== card.blobName));
        setConfirmDeleteCard(null);
        if (lightboxCard?.blobName === card.blobName) setLightboxCard(null);
    };

    const handleSignOut = async () => {
        try {
            await fetch('/api/sessionLogout', { method: 'POST' });
            await signOut(auth);
            //('User signed out successfully!');
            localStorage.removeItem('signed_in_id');
            localStorage.removeItem('signed_in');
            localStorage.removeItem('signed_in_email');
            localStorage.removeItem('signed_in_fcm_token');
            setAppData({ ...appData, user: { ...appData.user, signedIn: false, userEmail: '', userId: '' } });
        } catch (error: any) {
            console.error('Error signing out:', error.message);
        }
        navigate('/');
        setAppData({ ...appData, user: { ...appData.user, signedIn: false } });
    };

    const handleDeleteAccount = async () => {
        setDeleting(true);
        try {
            // 1. Flag as deleted in Azure + delete Firebase Auth record (server-side)
            await fetch('/api/user', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId: appData.user.userId }),
            });
            // 2. Sign out the Firebase client session
            const currentUser = auth.currentUser;
            if (currentUser) await firebaseDeleteUser(currentUser);
        } catch (err) {
            console.error('Error deleting account:', err);
        } finally {
            setDeleting(false);
        }
        // Clear local state regardless
        localStorage.clear();
        setAppData({ ...appData, user: { ...appData.user, signedIn: false, userEmail: '', userId: '' } });
        navigate('/');
    };

    const tabs = [
        { key: 'profile', icon: 'Settings', label: 'Settings' },
        // { key: 'friends', icon: 'PeopleAdd',    label: 'Friends' },
        { key: 'cards',   icon: 'PaymentCard',  label: 'Cards' },
        { key: 'scons',   icon: 'Trophy',        label: "sCoN's" },
        // { key: 'logs',    icon: 'RowsGroup',    label: 'Logs' }
    ] as const;
    type TabKey = 'profile' | 'friends' | 'cards' | 'logs' | 'scons';
    const [activeTab, setActiveTab] = useState<TabKey>('profile');

    const loadScons = async () => {
        if (!appData.user.userId) return;
        setSconsLoading(true);
        const result = await _sConsciousApiService.getContributions(appData.user.userId);
        setScons(result);
        setSconsLoading(false);
    };

    const handleTabClick = (key: TabKey) => {
        setActiveTab(key);
        if (key === 'cards' && fidelityCards.length === 0 && !cardsLoading) loadFidelityCards();
        if (key === 'scons' && scons === null && !sconsLoading) loadScons();
    };

    return (
        <div className="user-layout">
            {/* ── Public profile ── */}
            <div style={{ textAlign: 'center', padding: '20px 16px 0', width: '100%' }}>
                {/* Avatar */}
                <div
                    onClick={() => {
                        if (uploadingPhoto) return;
                        if (appData.user.photoUrl) { setShowPhotoLightbox(true); setConfirmDeletePhoto(false); }
                        else fileInputRef.current?.click();
                    }}
                    title={appData.user.photoUrl ? 'View profile photo' : 'Upload profile photo'}
                    style={{
                        width: 80, height: 80, borderRadius: '50%',
                        background: '#222', border: '3px solid rgba(0,0,0,0.15)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: uploadingPhoto ? 'default' : 'pointer',
                        overflow: 'hidden', margin: '0 auto 12px',
                        position: 'relative',
                    }}
                >
                    {appData.user.photoUrl ? (
                        <img src={appData.user.photoUrl} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                        <span style={{ fontSize: 32, color: '#ffcc00', fontWeight: 700, lineHeight: 1 }}>
                            {(editName || appData.user.userEmail || '?')[0].toUpperCase()}
                        </span>
                    )}
                    {uploadingPhoto && (
                        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FontIcon iconName="Sync" style={{ color: '#ffcc00', fontSize: 24 }} />
                        </div>
                    )}
                </div>

                {/* Photo lightbox */}
                {showPhotoLightbox && appData.user.photoUrl && (
                    <div className="fidelity-lightbox" onClick={() => { setShowPhotoLightbox(false); setConfirmDeletePhoto(false); }}>
                        <div className="fidelity-lightbox__inner" onClick={e => e.stopPropagation()}>
                            <div className="fidelity-lightbox__actions">
                                {confirmDeletePhoto ? (
                                    <div style={{ display: 'flex', gap: 4, alignItems: 'center', background: 'rgba(0,0,0,0.7)', borderRadius: 20, padding: '4px 8px' }}>
                                        <IconButton
                                            iconProps={{ iconName: 'CheckMark' }}
                                            title="Yes, delete"
                                            ariaLabel="Yes, delete"
                                            disabled={deletingPhoto}
                                            onClick={async () => { await handleDeletePhoto(); setShowPhotoLightbox(false); setConfirmDeletePhoto(false); }}
                                            styles={{ root: { backgroundColor: '#e53935', color: '#fff', borderRadius: '50%', width: 38, height: 38 }, rootHovered: { backgroundColor: '#c62828', color: '#fff' }, rootPressed: { backgroundColor: '#b71c1c', color: '#fff' }, icon: { color: '#fff' } }}
                                        />
                                        <IconButton
                                            iconProps={{ iconName: 'Cancel' }}
                                            title="Cancel"
                                            ariaLabel="Cancel"
                                            onClick={() => setConfirmDeletePhoto(false)}
                                            styles={{ root: { backgroundColor: '#ffcc00', color: '#111', borderRadius: '50%', width: 38, height: 38 }, rootHovered: { backgroundColor: '#e6b800', color: '#111' }, rootPressed: { backgroundColor: '#ccaa00', color: '#111' }, icon: { color: '#111' } }}
                                        />
                                    </div>
                                ) : (
                                    <IconButton
                                        iconProps={{ iconName: 'Delete' }}
                                        title="Delete photo"
                                        ariaLabel="Delete photo"
                                        onClick={() => setConfirmDeletePhoto(true)}
                                        styles={{
                                            root: { backgroundColor: '#ffcc00', color: '#000', borderRadius: '50%', width: 36, height: 36 },
                                            rootHovered: { backgroundColor: '#e6b800', color: '#000' },
                                            rootPressed: { backgroundColor: '#ccaa00', color: '#000' },
                                        }}
                                    />
                                )}
                                <button className="closeBtn" onClick={() => { setShowPhotoLightbox(false); setConfirmDeletePhoto(false); }}>✕</button>
                            </div>
                            <img src={appData.user.photoUrl} alt="Profile" className="fidelity-lightbox__img" style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain' }} />
                        </div>
                    </div>
                )}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handlePhotoChange}
                />

                {/* Name */}
                <div style={{ fontWeight: 800, fontSize: 20, marginBottom: 4 }}>{editName || appData.user.userName}</div>

                {/* Stats grid */}
                {selfStats && (
                    <div style={{
                        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8,
                        padding: '12px 0 4px', maxWidth: 400, margin: '0 auto',
                    }}>
                        {[
                            { label: 'Receipts', value: selfStats.receipts_count, icon: 'Dataflows' },
                            { label: 'Products', value: selfStats.items_count, icon: 'Product' },
                            { label: "sCoN's", value: selfStats.scons_points, icon: 'Trophy' },
                            { label: 'Friends', value: selfStats.friends_count, icon: 'People' },
                        ].map(stat => (
                            <div key={stat.label} style={{
                                background: 'rgba(0,0,0,0.08)', borderRadius: 16, padding: '10px 8px',
                                textAlign: 'center',
                            }}>
                                <FontIcon iconName={stat.icon} style={{ fontSize: 14, color: 'rgba(0,0,0,0.35)', display: 'block', marginBottom: 2 }} />
                                <div style={{ fontWeight: 800, fontSize: 18, lineHeight: 1.2 }}>{stat.value}</div>
                                <div style={{ fontSize: 10, color: 'rgba(0,0,0,0.45)', fontWeight: 600, marginTop: 1 }}>{stat.label}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* ── Tab nav ── */}
            <div className="user-nav">
                {tabs.map(tab => (
                    <FontIcon
                        key={tab.key}
                        iconName={tab.icon}
                        aria-label={tab.label}
                        title={tab.label}
                        className={`FontIcon user-nav__btn${activeTab === tab.key ? ' user-nav__btn--active' : ''}`}
                        onClick={() => handleTabClick(tab.key)}
                    />
                ))}
            </div>

            {/* ── Tab content ── */}
            <div className="user-content">
                {activeTab === 'profile' && (
                    <>
                        <label style={{ fontSize: '0.9rem' }}>{appData.user.currency ? `Welcome back, ${editName}` : `Welcome, ${editName || 'new user'}! Please set your home currency.`}</label>
                        <br />
                        <input
                            type="text"
                            value={editName}
                            onChange={e => setEditName(e.target.value)}
                            placeholder="Your name"
                            className="darkInput"
                            style={{ marginTop: 6, marginBottom: 16, width: 200 }}
                        />
                        <br />
                        <label style={{ fontSize: '0.9rem' }}>Home currency</label>
                        <br />
                        <SelectComponent
                            value={editCurrency}
                            onChange={handleCurrencyChange}
                            options={currencies.map(c => ({ value: c.code, label: `${c.code} - ${c.name}` }))}
                            height="2rem"
                            style={{ marginTop: 6, marginBottom: 16 }}
                        />
                        <br />
                        <label
                            style={{ fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', cursor: 'pointer' }}
                            onClick={() => {
                                const next = !editDiscoverable;
                                setEditDiscoverable(next);
                                setAppData({ ...appData, user: { ...appData.user, discoverable: next } });
                                fetch('/api/updateUser', {
                                    method: 'PATCH',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ userId: appData.user.userId, discoverable: next }),
                                }).catch(() => {});
                            }}
                        >
                            <span
                                style={{
                                    width: 20, height: 20, borderRadius: 4,
                                    border: '2px solid rgba(0,0,0,0.35)',
                                    background: editDiscoverable ? '#000' : 'transparent',
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    flexShrink: 0, transition: 'background 0.15s',
                                }}
                            >
                                {editDiscoverable && <span style={{ color: '#ffcc00', fontWeight: 900, fontSize: 14, lineHeight: 1 }}>✓</span>}
                            </span>
                            Be found in people
                        </label>
                        <br />
                        <ActionButton
                            label={saving ? 'Saving...' : 'Save'}
                            onClick={handleSaveProfile}
                            ariaLabel="Save profile"
                            icon={<MdSave style={{ color: '#ffcc00' }} />}
                        />
                        <br />
                        <br />
                        <ActionButton
                            label="Sign Out"
                            onClick={handleSignOut}
                            ariaLabel="Sign Out"
                            icon={<FaSignOutAlt style={{ color: '#ffcc00' }} />}
                        />
                        <br />
                        <br />
                        {!confirmDelete ? (
                            <ActionButton
                                label="Delete account"
                                onClick={() => setConfirmDelete(true)}
                                ariaLabel="Delete account"
                                style={{ background: 'rgba(180,0,0,0.15)', color: '#8b0000', fontSize: '0.78rem' }}
                            />
                        ) : (
                            <div style={{ border: '1px solid rgba(180,0,0,0.3)', borderRadius: 8, padding: '12px 16px', maxWidth: 280, margin: '0 auto', background: 'rgba(180,0,0,0.07)' }}>
                                <p style={{ margin: '0 0 10px', fontSize: '0.85rem', fontWeight: 600 }}>Delete your account?</p>
                                <p style={{ margin: '0 0 12px', fontSize: '0.78rem' }}>Your login will be permanently removed.</p>
                                <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                                    <ActionButton
                                        label={deleting ? 'Deleting…' : 'Yes, delete'}
                                        onClick={handleDeleteAccount}
                                        style={{ background: 'rgba(180,0,0,0.2)', color: '#8b0000' }}
                                    />
                                    <ActionButton
                                        label="Cancel"
                                        onClick={() => setConfirmDelete(false)}
                                    />
                                </div>
                            </div>
                        )}
                    </>
                )}
                {/* {activeTab === 'logs' && <History />} */}
                {/* {activeTab === 'friends' && <Friends onStartChat={u => { navigate(`/Chat?userId=${encodeURIComponent(u.user_id)}&userName=${encodeURIComponent(u.user_name)}&userEmail=${encodeURIComponent(u.user_email)}`); }} />} */}
                {activeTab === 'scons' && (
                    <div style={{ textAlign: 'center', padding: '0 8px' }}>
                        {sconsLoading || scons === null ? (
                            <div style={{ opacity: 0.5, marginTop: 32 }}>
                                <FontIcon iconName="Sync" style={{ fontSize: 24 }} /> Loading…
                            </div>
                        ) : (
                            <>
                                {/* Score badge */}
                                <div style={{ margin: '28px auto 24px', display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 4, background: 'rgba(0,0,0,0.08)', borderRadius: 20, padding: '20px 40px' }}>
                                    <div style={{ fontSize: '3.5rem', fontWeight: 900, lineHeight: 1 }}>
                                        {scons.totalPoints}
                                    </div>
                                    <div style={{ fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.1em', opacity: 0.55 }}>
                                        sCoN's
                                    </div>
                                </div>
                                {/* Total AI tokens */}
                                {(() => {
                                    const receiptTokens = (appData?.data?.reportReceipts ?? []).reduce((sum: number, r: any) => sum + (r.tokens_used ?? 0), 0);
                                    const lookupTokens = (appData?.data as any)?.lookup_tokens_used ?? 0;
                                    const totalTokens = receiptTokens + lookupTokens;
                                    return totalTokens > 0 ? (
                                        <div style={{ margin: '-12px auto 20px', fontSize: '0.78rem', opacity: 0.5 }}>
                                            {totalTokens.toLocaleString()} AI tokens used
                                        </div>
                                    ) : null;
                                })()}
                                {/* History */}
                                <div style={{ textAlign: 'left', marginTop: 8 }}>
                                    {scons.contributions.length === 0 ? (
                                        <p style={{ textAlign: 'center', opacity: 0.5, marginTop: 16, fontSize: '0.85rem' }}>No contributions yet. Upload a receipt to earn your first sCoN's!</p>
                                    ) : (
                                        [...scons.contributions]
                                            .sort((a, b) => b.created_at.localeCompare(a.created_at))
                                            .map(c => (
                                                <div key={c.contribution_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(0,0,0,0.06)', borderRadius: 10, padding: '10px 14px', marginBottom: 6 }}>
                                                    <div>
                                                        <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                                                            {(c.type as string) === 'receipt_upload' ? 'Receipt scanned' : (c.type as string) === 'brand_scan' ? 'Product photo scanned' : (c.type as string) === 'reanalysis' ? 'Receipt re-analyzed' : (c.type as string) === 'corp_update' ? 'Corporation updated' : 'Brand updated'}
                                                        </div>
                                                        <div style={{ fontSize: '0.72rem', opacity: 0.5, marginTop: 2 }}>
                                                            {new Date(c.created_at).toLocaleDateString()}
                                                        </div>
                                                    </div>
                                                    <div style={{ background: '#000', color: '#ffcc00', borderRadius: 20, padding: '4px 12px', fontWeight: 800, fontSize: '0.85rem', flexShrink: 0 }}>
                                                        +{c.points}
                                                    </div>
                                                </div>
                                            ))
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                )}
                {activeTab === 'cards' && (
                    <div className="fidelity-cards">
                        {/* Add card / capture */}
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                            <button
                                className="actionButton"
                                onClick={() => setShowCardCamera(true)}
                                style={{ gap: 6 }}
                            >
                                <FontIcon iconName="Camera" style={{ color: '#ffcc00' }} />
                                <span>Add fidelity card</span>
                            </button>
                        </div>

                        {/* Camera overlay */}
                        {showCardCamera && (
                            <div style={{ position: 'fixed', inset: 0, zIndex: 1100 }}>
                                <Camera onCapture={handleCardCapture} onClose={() => setShowCardCamera(false)} />
                            </div>
                        )}

                        {/* Pending card preview + name input */}
                        {pendingCardImg && (
                            <div className="fidelity-card-pending">
                                <img src={pendingCardImg} alt="Card preview" className="fidelity-card-pending__img" />
                                <input
                                    type="text"
                                    className="darkInput"
                                    placeholder="Card name (optional)"
                                    value={pendingCardName}
                                    onChange={e => setPendingCardName(e.target.value)}
                                    style={{ width: '100%', marginTop: 10, marginBottom: 10 }}
                                />
                                <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                                    <button
                                        className="actionButton"
                                        onClick={handleSaveCard}
                                        disabled={savingCard}
                                    >
                                        <FontIcon iconName="Save" style={{ color: '#ffcc00', marginRight: 4 }} />
                                        {savingCard ? 'Saving…' : 'Save'}
                                    </button>
                                    <button
                                        className="actionButton"
                                        onClick={() => { setPendingCardImg(null); setPendingCardName(''); }}
                                        disabled={savingCard}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Cards grid */}
                        {cardsLoading ? (
                            <div style={{ textAlign: 'center', opacity: 0.6, marginTop: 24 }}>
                                <FontIcon iconName="Sync" style={{ fontSize: 24 }} /> Loading…
                            </div>
                        ) : fidelityCards.length === 0 && !pendingCardImg ? (
                            <p style={{ textAlign: 'center', opacity: 0.5, marginTop: 24 }}>No cards yet. Tap "Add card" to capture one.</p>
                        ) : (
                            <div className="fidelity-cards__grid">
                                {fidelityCards.map(card => (
                                    <div key={card.blobName} className="fidelity-card-thumb" onClick={() => { setLightboxCard(card); setConfirmDeleteCard(null); }}>
                                        <img src={card.url} alt={card.name || 'Card'} className="fidelity-card-thumb__img" />
                                        <div className="fidelity-card-thumb__label">{card.name || <em style={{ opacity: 0.5 }}>Unnamed</em>}</div>
                                        <button
                                            className="fidelity-card-thumb__delete"
                                            title="Delete card"
                                            onClick={e => { e.stopPropagation(); setConfirmDeleteCard(card); setLightboxCard(card); }}
                                        >
                                            <FontIcon iconName="Delete" style={{ fontSize: 13 }} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}



                        {/* Lightbox */}
                        {lightboxCard && (
                            <div className="fidelity-lightbox" onClick={() => setLightboxCard(null)}>
                                <div className="fidelity-lightbox__inner" onClick={e => e.stopPropagation()}>
                                    {/* Top-right action bar: delete + close */}
                                    <div className="fidelity-lightbox__actions">
                                        {confirmDeleteCard ? (
                                            <div style={{ display: 'flex', gap: 4, alignItems: 'center', background: 'rgba(0,0,0,0.7)', borderRadius: 20, padding: '4px 8px' }}>
                                                <IconButton
                                                    iconProps={{ iconName: 'CheckMark' }}
                                                    title="Yes, delete"
                                                    ariaLabel="Yes, delete"
                                                    onClick={() => { handleDeleteCard(confirmDeleteCard); setLightboxCard(null); }}
                                                    styles={{ root: { backgroundColor: '#e53935', color: '#fff', borderRadius: '50%', width: 28, height: 28 }, rootHovered: { backgroundColor: '#c62828', color: '#fff' }, rootPressed: { backgroundColor: '#b71c1c', color: '#fff' }, icon: { color: '#fff' } }}
                                                />
                                                <IconButton
                                                    iconProps={{ iconName: 'Cancel' }}
                                                    title="Cancel"
                                                    ariaLabel="Cancel"
                                                    onClick={() => setConfirmDeleteCard(null)}
                                                    styles={{ root: { backgroundColor: '#ffcc00', color: '#111', borderRadius: '50%', width: 38, height: 38, boxShadow: '0 2px 12px rgba(0,0,0,0.35)' }, rootHovered: { backgroundColor: '#e6b800', color: '#111' }, rootPressed: { backgroundColor: '#ccaa00', color: '#111' }, icon: { color: '#111' } }}
                                                />
                                            </div>
                                        ) : (
                                            <IconButton
                                                iconProps={{ iconName: 'Delete' }}
                                                title="Delete card"
                                                ariaLabel="Delete card"
                                                onClick={() => setConfirmDeleteCard(lightboxCard)}
                                                styles={{
                                                    root: { backgroundColor: '#ffcc00', color: '#000', borderRadius: '50%', width: 36, height: 36 },
                                                    rootHovered: { backgroundColor: '#e6b800', color: '#000' },
                                                    rootPressed: { backgroundColor: '#ccaa00', color: '#000' },
                                                }}
                                            />
                                        )}
                                        <button className="closeBtn" onClick={() => { setLightboxCard(null); setConfirmDeleteCard(null); }}>✕</button>
                                    </div>
                                    <img src={lightboxCard.url} alt={lightboxCard.name || 'Card'} className="fidelity-lightbox__img" />
                                    {lightboxCard.name && <p className="fidelity-lightbox__name">{lightboxCard.name}</p>}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
